/**
 * Backup export/import — §13.3.
 *
 * This is data we did NOT produce: it may have been hand-edited, truncated, or
 * written by a different version. Every import is fully validated.
 */
import { z } from 'zod'
import { BACKUP_VERSION } from './constants'
import type { Bookmark, Note, PageState, SrsCard } from './types'
import type { Program } from './scheduler'

const noteSchema = z.object({
  verseKey: z.string(),
  text: z.string(),
  updatedAt: z.number(),
})

const bookmarkSchema = z.object({
  verseKey: z.string(),
  createdAt: z.number(),
})

const pageStateSchema = z.object({
  page: z.number().int().min(1).max(604),
  read: z.boolean(),
  memorised: z.boolean(),
  readOn: z.number().nullable(),
})

const srsCardSchema = z.object({
  page: z.number().int().min(1).max(604),
  ease: z.number(),
  interval: z.number(),
  reps: z.number(),
  lapses: z.number(),
  due: z.number(),
})

const linePositionSchema = z.object({
  page: z.number().int().min(1).max(604),
  line: z.number().int().min(1),
})

const scopeSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('mushaf') }),
  z.object({ type: z.literal('juz'), from: z.number().int(), to: z.number().int() }),
  z.object({ type: z.literal('surah'), from: z.number().int(), to: z.number().int() }),
  z.object({ type: z.literal('pages'), from: z.number().int(), to: z.number().int() }),
])

const volumeSegmentSchema = z.object({
  from: z.number().int(),
  unit: z.enum(['line', 'page', 'ayah', 'juz']),
  amount: z.number(),
  review: z.number(),
})

const programSchema = z.object({
  id: z.string(),
  methodId: z.string(),
  name: z.string(),
  kind: z.enum(['hifz', 'murajaah', 'tilawah']),
  scope: scopeSchema,
  volumes: z.array(volumeSegmentSchema).min(1),
  cursor: linePositionSchema,
  /**
   * Records written before this field existed normalise to page 1 at the storage
   * boundary, so a legacy file does not fail to load over a field it never had.
   */
  startAt: linePositionSchema.optional(),
  restDays: z.array(z.number().int().min(0).max(6)),
  maxCarryOver: z.number(),
  state: z.enum(['active', 'paused', 'done']),
  startedOn: z.number().int(),
  createdAt: z.number(),
})

/** The legacy single-plan khatmah that v1 stored instead of a programme. */
const legacyKhatmahSchema = z.object({
  pagesPerDay: z.number().optional(),
  startedOn: z.number().optional(),
  cursorPage: z.number().optional(),
})

/**
 * VERSIONS MUST BE A DISCRIMINATED UNION, NEVER A LITERAL.
 *
 * v1 used `z.literal(1)`, which REFUSES any other version — so bumping the format
 * that way would have rejected every backup already sitting in a user's downloads
 * folder. Parse old files against their own shape and upgrade them; callers only ever
 * see the current shape.
 */
const backupV1Schema = z.object({
  version: z.literal(1),
  exportedAt: z.number().optional(),
  notes: z.array(noteSchema).default([]),
  bookmarks: z.array(bookmarkSchema).default([]),
  pages: z.array(pageStateSchema).default([]),
  srs: z.record(z.string(), srsCardSchema).default({}),
  khatmah: legacyKhatmahSchema.nullish(),
})

const backupV2Schema = z.object({
  version: z.literal(2),
  exportedAt: z.number().optional(),
  notes: z.array(noteSchema).default([]),
  bookmarks: z.array(bookmarkSchema).default([]),
  pages: z.array(pageStateSchema).default([]),
  srs: z.record(z.string(), srsCardSchema).default({}),
  programs: z.array(programSchema).default([]),
  activeProgram: z.string().nullable().default(null),
})

const backupSchema = z.discriminatedUnion('version', [backupV1Schema, backupV2Schema])

export const SUPPORTED_BACKUP_VERSIONS = [1, 2] as const

export interface Backup {
  version: typeof BACKUP_VERSION
  exportedAt: number
  notes: Note[]
  bookmarks: Bookmark[]
  pages: PageState[]
  srs: Record<string, SrsCard>
  programs: Program[]
  activeProgram: string | null
}

/**
 * I3: a failure returns a REASON, not a sentence. The caller needs to know WHICH of the
 * three things went wrong — a composed Arabic message cannot be branched on, and the
 * wording belongs to the reader (`apps/web/src/content/backup.ts`).
 */
export type ImportFailure = 'unsupported' | 'invalid' | 'notJson'

export type ImportResult =
  | { ok: true; backup: Backup; upgradedFrom: number | null }
  | { ok: false; reason: ImportFailure; issues: string[] }

/** Fill in `startAt` for records written before the field existed (§11.2). */
function normaliseProgram(p: z.infer<typeof programSchema>): Program {
  return { ...p, startAt: p.startAt ?? { page: 1, line: 1 } }
}

/**
 * Return a RESULT, not an exception — a bad import is an ordinary user outcome (wrong
 * file picked, truncated download), and the caller needs the issue list.
 */
export function parseBackup(raw: string): ImportResult {
  let json: unknown
  try {
    json = JSON.parse(raw)
  } catch {
    return { ok: false, reason: 'notJson', issues: [] }
  }

  /**
   * Check the version BEFORE parsing. A discriminated-union miss reports as a generic
   * union failure, and "unsupported version" is by far the most likely reason a file
   * will not load.
   */
  const version = (json as { version?: unknown } | null)?.version
  if (typeof version !== 'number' || !SUPPORTED_BACKUP_VERSIONS.includes(version as 1 | 2)) {
    return { ok: false, reason: 'unsupported', issues: [`version: ${String(version)}`] }
  }

  const parsed = backupSchema.safeParse(json)
  if (!parsed.success) {
    const issues = parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
    return { ok: false, reason: 'invalid', issues }
  }

  const data = parsed.data
  const common = {
    exportedAt: data.exportedAt ?? Date.now(),
    notes: data.notes,
    bookmarks: data.bookmarks,
    pages: data.pages,
    srs: data.srs,
  }

  if (data.version === 2) {
    return {
      ok: true,
      upgradedFrom: null,
      backup: {
        version: BACKUP_VERSION,
        ...common,
        programs: data.programs.map(normaliseProgram),
        activeProgram: data.activeProgram,
      },
    }
  }

  // v1 → v2: convert the single khatmah plan into a `tilawah` programme. `startAt` is
  // page 1 so it keeps reporting the same fraction of the mushaf it always did.
  const programs: Program[] = []
  let activeProgram: string | null = null
  if (data.khatmah) {
    const id = 'khatmah-legacy'
    const startedOn = data.khatmah.startedOn ?? Math.floor(Date.now() / 86_400_000)
    programs.push({
      id,
      methodId: 'tilawah',
      /**
       * I3 EXCEPTION, deliberate: this is not copy RETURNED to a caller, it is a value
       * WRITTEN INTO the user's library — `Program.name` is stored and user-editable.
       * A migration cannot leave it blank, and it cannot reach into the reader's
       * dictionary, so the seed name lives here. It matches `METHOD_COPY.tilawah.name`.
       */
      name: 'ختمة تلاوة',
      kind: 'tilawah',
      scope: { type: 'mushaf' },
      volumes: [
        { from: startedOn, unit: 'page', amount: data.khatmah.pagesPerDay ?? 4, review: 0 },
      ],
      cursor: { page: data.khatmah.cursorPage ?? 1, line: 1 },
      startAt: { page: 1, line: 1 },
      restDays: [],
      maxCarryOver: 3,
      state: 'active',
      startedOn,
      createdAt: Date.now(),
    })
    activeProgram = id
  }

  return {
    ok: true,
    upgradedFrom: 1,
    backup: { version: BACKUP_VERSION, ...common, programs, activeProgram },
  }
}

export function serialiseBackup(
  backup: Omit<Backup, 'version' | 'exportedAt'>,
  now: number,
): string {
  const payload: Backup = { version: BACKUP_VERSION, exportedAt: now, ...backup }
  return JSON.stringify(payload, null, 2)
}

export function backupFilename(now: number): string {
  const d = new Date(now)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `mushaf-backup-${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}.json`
}
